import React, { useContext, useEffect, useMemo } from 'react';
import { Route, Switch, Redirect, useLocation, useHistory } from 'react-router-dom';
import { FamilyEnrollmentView, FamilyEnrollmentContext } from 'features/family-enrollment/context';
import { Routes } from 'routes';
import * as formSteps from 'features/family-enrollment/steps';
import { FamilyLanding, useEnrollmentCodeFromUrl } from './FamilyLanding';
import { JoinProgramLanding } from './JoinProgramLanding';
import { Head } from 'components/head/Head';
import { Btn } from 'components/btn/Btn';
import logo from 'media/images/RaiseRightLogo.svg';
import flipGiveLogo from 'media/images/flipgive/FlipGiveLogo.svg';
import { Branding, IS_FLIPGIVE } from 'common/env';
import { createStyles } from 'utils/createStyle';
import {
    faUser as faUserSelected,
    faPenAlt as faPenAltSelected,
    faLockAlt as faLockAltSelected,
    faMobileAlt as faMobileAltSelected,
} from '@fortawesome/pro-solid-svg-icons';
import { faUser, faPenAlt, faLockAlt, faMobileAlt } from '@fortawesome/pro-light-svg-icons';
import {
    IStep,
    LitePlusStepper,
} from 'features/coordinator/enroll/steps/LitePlusStepper/LitePlusStepper';
import { Metadata } from 'components/metadata';
import { MetadataPage } from 'components/metadata/constants';
import { ActivityIndicator } from 'components/activity-indicator/ActivityIndicator';
import {
    getSocialRegistrationResumeParams,
    getSocialRegistrationErrorMessage,
    exchangeFamilyEnrollmentSocialRegistrationSession,
    restoreFamilyEnrollmentSocialRegistrationState,
    clearFamilyEnrollmentSocialRegistrationState,
    normalizeSocialRegistrationProvider,
} from './socialRegistration';
import { RegistrationRequest } from 'common/api/auth/models/RegistrationRequest';
import { ShopScreen } from 'features/shop-base/ShopScreen';
import BrandsScroller from './BrandsScroller';
import {
    logOrgEnrollmentV2,
    logParticipantEnrollment,
    logStartAProgramSelected,
} from 'config/analytics';
import { useRootSelector } from 'common/features/featuresReducer';
import { selectLoginState } from 'common/features/store/duck/account/duck';

const styles = createStyles({
    masthead: 'shadow-lg flex justify-center pt-5 pb-3 sm:pt-6 sm:pb-4',
    logo: 'w-40 sm:w-60',
    flipGiveLogo: 'w-28 sm:w-44',
});

const EnrollPageVariant = () => {
    const history = useHistory();
    const location = useLocation();

    const onStartFundraiser = () => {
        logOrgEnrollmentV2('org_enrollment:start_program_clicked', { entry_target: 'v2' });
        logStartAProgramSelected('variant');
        history.push({ pathname: Routes.EnrollV2, search: location.search });
    };

    const onJoinFundraiser = () => {
        // Fires before `program_join_viewed` (which fires on JoinProgramLanding mount after navigation).
        // PostHog funnels must not require strict chronological order between these two events for
        // welcome-path variant users, or this slice will drop out of the funnel.
        logParticipantEnrollment(2, 'join_a_program_selected', {
            flowVariant: 'variant',
            experimentName: 'web_enrollment_test',
            experimentVariant: 'variant',
        });
        history.push({ pathname: Routes.EnrollFamily, search: location.search });
    };

    return (
        <ShopScreen>
            <div className="w-full flex flex-col items-center mx-auto">
                <div className="w-full px-auto max-w-[640px] flex flex-col items-center pt-[60px] pb-[48px] lg:pt-[100px] lg:pb-[80px]">
                    <h1 className="mx-auto max-w-[817px] text-center font-semibold text-brand-dark leading-[1.2] text-[32px] tracking-[-0.64px] sm:text-[40px] sm:tracking-[-0.8px] md:text-[48px] lg:text-[56px] lg:tracking-[-1.12px] mb-5 md:mb-6">
                        Welcome to {Branding}
                    </h1>
                    <p className="mx-auto max-w-[540px] text-center font-normal text-grey-darkest text-[24px] leading-[1.4] mb-8 md:mb-10 px-4">
                        Join 50,000+ organizations and families that have raised over{' '}
                        {IS_FLIPGIVE ? '$50 million' : '$900 million'}.
                    </p>

                    <div className="flex flex-col md:flex-row gap-5 w-full max-w-sm md:max-w-full px-4">
                        <Btn
                            onClick={onStartFundraiser}
                            className="w-full md:flex-1 whitespace-nowrap flex items-center justify-center h-[56px] min-h-[56px] lg:h-[72px] lg:min-h-[72px] px-7 !py-0 text-[20px] leading-[28px] !border !border-solid !border-[#005E9D]"
                        >
                            Start a New Program
                        </Btn>
                        <Btn
                            isSecondary
                            onClick={onJoinFundraiser}
                            className="w-full md:flex-1 whitespace-nowrap flex items-center justify-center h-[56px] min-h-[56px] lg:h-[72px] lg:min-h-[72px] px-7 !py-0 text-[20px] leading-[28px] border-2 border-[#005E9D] text-[#005E9D]"
                        >
                            Join an Existing Program
                        </Btn>
                    </div>
                </div>

                <BrandsScroller />
            </div>
        </ShopScreen>
    );
};

const EnrollIndexRoute = () => {
    const { isEnrollmentTestVariant } = useContext(FamilyEnrollmentContext);
    const enrollmentCode = useEnrollmentCodeFromUrl();
    const history = useHistory();
    const location = useLocation();
    const code = enrollmentCode?.trim();
    const isUserLoggedIn = useRootSelector(selectLoginState);

    useEffect(() => {
        if (!isEnrollmentTestVariant || !code) {
            return;
        }
        const qs = new URLSearchParams(location.search);
        if (!qs.get('enrollCode')) {
            qs.set('enrollCode', code);
        }
        const search = qs.toString() ? `?${qs.toString()}` : '';
        history.replace({
            pathname: Routes.EnrollFamily,
            search,
            hash: location.hash,
            state: location.state,
        });
    }, [isEnrollmentTestVariant, code, history, location.hash, location.search, location.state]);

    useEffect(() => {
        if (!isUserLoggedIn) {
            return;
        }

        const currentParams = new URLSearchParams(window.location.search);
        const search = currentParams.toString() ? `?${currentParams.toString()}` : '';
        history.replace({
            pathname: Routes.ShopAccountSettingsPersonalInformationTab,
            search,
        });
    }, [history, isUserLoggedIn]);

    if (isEnrollmentTestVariant && code) {
        return null;
    }
    if (isEnrollmentTestVariant) {
        return <EnrollPageVariant />;
    }
    return <FamilyLanding />;
};

const EnrollmentFlow = () => {
    const {
        isEnrollmentTestVariant,
        isSocialRegistration,
        isSocialRegistrationEnabled,
        updateUserAccountInfo,
        setOrganizationTo,
        setSocialRegistrationError,
        setSocialRegistrationExistingAccountProvider,
        isResumingSocialRegistration,
        setIsResumingSocialRegistration,
        goToStep,
    } = useContext(FamilyEnrollmentContext);

    const location = useLocation();
    const history = useHistory();

    useEffect(() => {
        const resumeParams = getSocialRegistrationResumeParams(location.search);
        const hasResumeParams = Boolean(
            resumeParams.outcome || resumeParams.externalRegistrationKey || resumeParams.provider
        );

        if (!hasResumeParams) {
            return;
        }

        let isMounted = true;
        const persistedState = restoreFamilyEnrollmentSocialRegistrationState();
        const clearCallbackParams = () =>
            history.replace({
                pathname: location.pathname,
                hash: location.hash,
                search: '',
            });

        const stopResuming = () => {
            if (isMounted) {
                setIsResumingSocialRegistration(false);
            }
        };

        const restoreEnrollmentState = () => {
            if (!persistedState) {
                return false;
            }

            updateUserAccountInfo(persistedState.userAccountInfo);
            setOrganizationTo(persistedState.organization);

            if (persistedState.enrollmentCode) {
                localStorage.setItem('RR_enrollmentCode', persistedState.enrollmentCode);
            }

            return true;
        };

        const handleResumeError = (message: string) => {
            restoreEnrollmentState();
            setSocialRegistrationError(message);
            clearFamilyEnrollmentSocialRegistrationState();
            clearCallbackParams();
            goToStep(Routes.EnrollFamilyGetStarted);
            stopResuming();
        };

        if (!persistedState) {
            handleResumeError(
                'We could not restore your enrollment details. Please continue through account setup again.'
            );
            return () => {
                isMounted = false;
            };
        }

        setIsResumingSocialRegistration(true);

        if (resumeParams.outcome === 'existing_account') {
            restoreEnrollmentState();
            setSocialRegistrationExistingAccountProvider(
                normalizeSocialRegistrationProvider(resumeParams.provider) || null
            );
            clearFamilyEnrollmentSocialRegistrationState();
            clearCallbackParams();
            goToStep(Routes.EnrollFamilyGetStarted);
            stopResuming();
            return () => {
                isMounted = false;
            };
        }

        if (
            resumeParams.outcome &&
            resumeParams.outcome !== 'success' &&
            resumeParams.outcome !== 'missing_email'
        ) {
            handleResumeError(getSocialRegistrationErrorMessage(resumeParams.outcome));
            return () => {
                isMounted = false;
            };
        }

        if (!resumeParams.externalRegistrationKey) {
            handleResumeError(getSocialRegistrationErrorMessage());
            return () => {
                isMounted = false;
            };
        }

        if (!persistedState.codeVerifier) {
            handleResumeError(getSocialRegistrationErrorMessage());
            return () => {
                isMounted = false;
            };
        }

        (async () => {
            try {
                const response = await exchangeFamilyEnrollmentSocialRegistrationSession(
                    resumeParams.externalRegistrationKey!,
                    persistedState.codeVerifier
                );

                if (!isMounted) {
                    return;
                }

                const socialEmail =
                    response.email || persistedState.userAccountInfo.socialEmail || '';
                const email = socialEmail || persistedState.userAccountInfo.email;

                updateUserAccountInfo((_prev: RegistrationRequest) => ({
                    ...persistedState.userAccountInfo,
                    firstName: response.firstName || persistedState.userAccountInfo.firstName,
                    lastName: response.lastName || persistedState.userAccountInfo.lastName,
                    email,
                    username: email,
                    registrationMethod: 'social',
                    provider: normalizeSocialRegistrationProvider(
                        response.provider || resumeParams.provider
                    ),
                    externalRegistrationKey: resumeParams.externalRegistrationKey,
                    codeVerifier: persistedState.codeVerifier,
                    socialEmail: socialEmail || undefined,
                    emailRequired: response.emailRequired ?? !response.email,
                }));
                setOrganizationTo(persistedState.organization);

                if (persistedState.enrollmentCode) {
                    localStorage.setItem('RR_enrollmentCode', persistedState.enrollmentCode);
                }

                clearFamilyEnrollmentSocialRegistrationState();
                clearCallbackParams();

                if (response.emailRequired || !response.email) {
                    goToStep(Routes.EnrollFamilyGetStarted);
                } else {
                    goToStep(Routes.EnrollFamilySocialEarningFor);
                }
            } catch (_error) {
                handleResumeError('We could not verify your sign-in. Please start sign-in again.');
                return;
            }

            stopResuming();
        })();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const socialSteps: IStep[] = useMemo(
        () => [
            {
                title: 'Account Details',
                icon: faUser,
                iconSelected: faUserSelected,
                routes: [Routes.EnrollFamilyGetStarted, Routes.EnrollFamilySocialEarningFor],
                exact: true,
            },
            {
                title: 'Account Security',
                icon: faLockAlt,
                iconSelected: faLockAltSelected,
                routes: [
                    Routes.EnrollFamilySecurityQuestions,
                    Routes.EnrollFamilySetupRecoveryPhone,
                    Routes.EnrollFamilyVerifyRecoveryPhone,
                ],
                exact: false,
            },
        ],
        []
    );

    const emailSteps: IStep[] = useMemo(
        () => [
            {
                title: 'Create Account',
                icon: faUser,
                iconSelected: faUserSelected,
                routes: [Routes.EnrollFamilyGetStarted, Routes.EnrollFamilyCreateAccount],
                exact: true,
            },
            {
                title: 'Account Details',
                icon: faPenAlt,
                iconSelected: faPenAltSelected,
                routes: [Routes.EnrollFamilyAccountDetails],
                exact: true,
            },
            {
                title: 'Account Security',
                icon: faLockAlt,
                iconSelected: faLockAltSelected,
                routes: [
                    Routes.EnrollFamilySecurityQuestions,
                    Routes.EnrollFamilySetupRecoveryPhone,
                    Routes.EnrollFamilyVerifyRecoveryPhone,
                ],
                exact: false,
            },
        ],
        []
    );

    // Social registration enabled: 3-step social flow, 4-step email/password flow
    const newSocialSteps: IStep[] = useMemo(
        () => [
            {
                title: 'Account Details',
                icon: faUser,
                iconSelected: faUserSelected,
                routes: [Routes.EnrollFamilyGetStarted, Routes.EnrollFamilySocialEarningFor],
                exact: true,
            },
            {
                title: 'Account Security',
                icon: faLockAlt,
                iconSelected: faLockAltSelected,
                routes: [Routes.EnrollFamilySecurityQuestions],
                exact: true,
            },
            {
                title: 'Setup Recovery',
                icon: faMobileAlt,
                iconSelected: faMobileAltSelected,
                routes: [
                    Routes.EnrollFamilySetupRecoveryPhone,
                    Routes.EnrollFamilyVerifyRecoveryPhone,
                ],
                exact: false,
            },
        ],
        []
    );

    const newEmailSteps: IStep[] = useMemo(
        () => [
            {
                title: 'Account Details',
                icon: faUser,
                iconSelected: faUserSelected,
                routes: [Routes.EnrollFamilyGetStarted, Routes.EnrollFamilyCreateAccount],
                exact: true,
            },
            {
                title: 'Create Password',
                icon: faPenAlt,
                iconSelected: faPenAltSelected,
                routes: [Routes.EnrollFamilyAccountDetails],
                exact: true,
            },
            {
                title: 'Account Security',
                icon: faLockAlt,
                iconSelected: faLockAltSelected,
                routes: [Routes.EnrollFamilySecurityQuestions],
                exact: true,
            },
            {
                title: 'Setup Recovery',
                icon: faMobileAlt,
                iconSelected: faMobileAltSelected,
                routes: [
                    Routes.EnrollFamilySetupRecoveryPhone,
                    Routes.EnrollFamilyVerifyRecoveryPhone,
                ],
                exact: false,
            },
        ],
        []
    );

    const flaggedSteps = isSocialRegistration ? newSocialSteps : newEmailSteps;
    const legacySteps = isSocialRegistration ? socialSteps : emailSteps;
    const steps = isSocialRegistrationEnabled ? flaggedSteps : legacySteps;
    const showStepper = location.pathname !== Routes.EnrollFamilyGetStarted;

    if (isResumingSocialRegistration) {
        return (
            <div className="mt-12 justify-center flex">
                <ActivityIndicator />
            </div>
        );
    }

    return (
        <Switch>
            <Route exact path={Routes.EnrollFamily}>
                {isEnrollmentTestVariant ? <JoinProgramLanding /> : <Redirect to={Routes.Enroll} />}
            </Route>
            <Route exact path={Routes.Enroll}>
                <Metadata page={MetadataPage.enroll}>
                    <EnrollIndexRoute />
                </Metadata>
            </Route>
            <>
                <Head title="Family Enrollment" />
                <header className={styles.masthead}>
                    <img
                        className={IS_FLIPGIVE ? styles.flipGiveLogo : styles.logo}
                        src={IS_FLIPGIVE ? flipGiveLogo : logo}
                        alt={Branding}
                    />
                </header>
                <main className="max-w-lg mx-auto">
                    {showStepper && <LitePlusStepper steps={steps} />}
                    <Route path={Routes.EnrollFamilyGetStarted}>
                        <formSteps.GetStartedStep />
                    </Route>
                    <Route path={Routes.EnrollFamilyCreateAccount}>
                        <formSteps.CreateFamilyAccount />
                    </Route>
                    <Route path={Routes.EnrollFamilySocialEarningFor}>
                        <formSteps.SocialEarningForStep />
                    </Route>
                    <Route path={Routes.EnrollFamilyAccountDetails}>
                        <formSteps.AccountDetailsStep />
                    </Route>
                    <Route path={Routes.EnrollFamilySignInInfo}>
                        <formSteps.SignInInfoStep />
                    </Route>
                    <Route path={Routes.EnrollFamilySecurityQuestions}>
                        <formSteps.SecurityQuestionStep />
                    </Route>
                    <Route path={Routes.EnrollFamilySetupRecoveryPhone}>
                        <formSteps.Setup2FaStep />
                    </Route>
                    <Route path={Routes.EnrollFamilyVerifyRecoveryPhone}>
                        <formSteps.VerifyPhone />
                    </Route>
                </main>
            </>
        </Switch>
    );
};

export const FamilyEnrollment = () => {
    return (
        <FamilyEnrollmentView>
            <EnrollmentFlow />
        </FamilyEnrollmentView>
    );
};
